import { useState } from "react";
import { AlertTriangle, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

export const ItemCodeTag = ({ code, testId }) => (code ? (
  <span className="inline-flex items-center rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] text-slate-700" data-testid={testId}>{code}</span>
) : (
  <span className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700" data-testid={testId}>
    <AlertTriangle className="w-3 h-3" /> Item code not set
  </span>
));

// Item code chip with an Admin-only inline editor; safe to place inside a <Link> card.
export function InlineItemCode({ model, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const open = (e) => { stop(e); setValue(model.item_code || ""); setEditing(true); };
  const save = async (e) => {
    stop(e);
    setSaving(true);
    try {
      const r = await api.patch(`/catalog/${model.id}/item-code`, { item_code: value });
      toast.success(r.data.item_code ? `Item code set to ${r.data.item_code}` : "Item code cleared");
      setEditing(false);
      onSaved?.(r.data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  if (editing) {
    return (
      <span className="flex items-center gap-1" onClick={stop}>
        <input autoFocus value={value} onChange={(e) => setValue(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === "Enter") save(e); if (e.key === "Escape") { stop(e); setEditing(false); } }}
          placeholder="e.g. HFT-R5-STD" className="h-7 w-full min-w-0 rounded border border-slate-300 px-2 font-mono text-xs uppercase focus:outline-none focus:ring-1 focus:ring-indigo-400" data-testid={`item-code-input-${model.id}`} />
        <button type="button" onClick={save} disabled={saving} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" aria-label="Save item code" data-testid={`item-code-save-${model.id}`}><Check className="w-4 h-4" /></button>
        <button type="button" onClick={(e) => { stop(e); setEditing(false); }} className="p-1 text-slate-400 hover:bg-slate-100 rounded" aria-label="Cancel"><X className="w-4 h-4" /></button>
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1">
      <ItemCodeTag code={model.item_code} testId={`item-code-${model.id}`} />
      {canEdit && (
        <button type="button" onClick={open} className="inline-flex items-center gap-0.5 text-[11px] text-indigo-600 hover:underline" data-testid={`item-code-edit-${model.id}`}>
          <Pencil className="w-3 h-3" /> {model.item_code ? "Edit" : "Set code"}
        </button>
      )}
    </span>
  );
}
